// What lies over the whole screen on a phone (a chat's side chat or side
// view, shell/phone.tsx `PhoneOverlay`): the old web's full screen overlay.
//
// Promise:
// - It covers the frame (the visible part of the screen, so it follows the
//   keyboard as the frame does: keyboard.ts), above the Dock; the screen under
//   it stays mounted as it was.
// - It has a Dock of its own: a post box in it takes the bottom row with the
//   close button at its right (where the round button is), and nothing goes
//   up from its left. Without a post box the close button stands alone where
//   the round button is.
// - What is inside puts nothing on the screen's Dock: its way up, its tools,
//   and its stowing stay inside (a Case shown in it does not change where the
//   screen's Dock goes up to).
// - The way out is the close button or the device's back (the side is a path).
// - Where a host draws the Dock (a native app), its bar, Dock, post box and
//   floating parts step aside while it is up (they stood over it: the chat's
//   post box and the Inbox's button over a document, 2026-11-02), and what is
//   inside draws its own, as in a browser. Under the status bar its top Dock
//   and body keep the room the screens do (phone.css `--phone-top`): a room
//   of its own on top of that put them a status bar lower.
import { m } from "@trameria/i18n";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { HostAsideContext } from "../shell/outside/floats";
import type { PhoneOverlayProps } from "../shell/phone";
import { GlassIconButton } from "../ui/glass";
import { Icon } from "../ui/icon";
import { PhoneContext, type PhoneState, usePhoneState } from "./context";
import { DOCK_SIDE } from "./items";
import { DOCK_BOTTOM } from "./layout";

const none = () => () => {};
const nothing = () => {};

export function PhoneOverlay({ onClose, children }: PhoneOverlayProps) {
  const outer = usePhoneState();
  const [inputs, setInputs] = useState(0);
  const [stows, setStows] = useState(0);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const claim = useCallback((kind: "cluster" | "tools" | "input") => {
    if (kind !== "input") return nothing;
    setInputs((n) => n + 1);
    return () => setInputs((n) => n - 1);
  }, []);
  const stow = useCallback(() => {
    setStows((n) => n + 1);
    return () => setStows((n) => n - 1);
  }, []);
  const close = useMemo(
    () => (
      <GlassIconButton
        label={m.shell_side_close()}
        onClick={() => closeRef.current()}
        width={49}
        height={48}
      >
        <Icon kind="close" size={18} />
      </GlassIconButton>
    ),
    [],
  );
  const state = useMemo<PhoneState | null>(
    () =>
      outer && {
        ...outer,
        hostDraws: false,
        up: null,
        claims: { cluster: 0, tools: 0, input: inputs },
        clusterHost: null,
        toolsHost: null,
        menuState: null,
        away: false,
        stowed: stows > 0,
        claim,
        stow,
        pushUp: none,
        openMenu: nothing,
        togglePanel: nothing,
        closeMenu: nothing,
        end: close,
      },
    [outer, inputs, stows, claim, stow, close],
  );
  const host = outer?.overlayHost;
  const shown = state !== null && host != null;
  const stowHost = outer?.hostDraws ? outer.stow : undefined;
  // frontend-development: not a fetch; the host's parts step aside while it is up.
  useLayoutEffect(
    () => (shown && stowHost ? stowHost() : undefined),
    [shown, stowHost],
  );
  if (!state || !host) return null;
  return createPortal(
    <div
      data-phone-overlay=""
      className="@container absolute inset-0 z-40 flex flex-col bg-surface text-fg motion-safe:slide-in-right"
    >
      <HostAsideContext.Provider value={outer?.hostDraws === true}>
        <PhoneContext.Provider value={state}>{children}</PhoneContext.Provider>
      </HostAsideContext.Provider>
      {inputs === 0 && (
        <div
          className="absolute z-10"
          style={{ right: DOCK_SIDE, bottom: DOCK_BOTTOM }}
        >
          {close}
        </div>
      )}
    </div>,
    host,
  );
}
